import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { EntityDetail } from '../components/common/EntityDetail';
import { DeleteDialog } from '../components/common/DeleteDialog';
import { useEntity } from '../hooks/useEntity';
import { useEntityMutation } from '../hooks/useEntityMutation';
import { useToast } from '../components/common/Toast';
import { RelatedEntitiesTabs } from '../components/common/RelatedEntitiesTabs';
import { getEntityConfig } from '../lib/entity-config';

export function EntityDetailPage() {
  const { entityName, id } = useParams<{ entityName: string; id: string }>();
  const navigate = useNavigate();
  const config = getEntityConfig(entityName ?? '');
  const { record, isLoading, error, notFound, refetch } = useEntity(entityName ?? '', id ?? '');
  const { deleteEntity } = useEntityMutation();
  const { showToast } = useToast();
  const [showDelete, setShowDelete] = useState(false);

  if (!config) return <div className="text-red-500">Unknown entity: {entityName}</div>;
  if (isLoading) return <div className="text-slate-500 py-8 text-center">Loading...</div>;

  // Fetch error — network/Lambda failure. Show error message with retry.
  if (error) {
    return (
      <div className="py-8 text-center">
        <div className="text-red-600 mb-4">
          <p className="font-semibold mb-1">Failed to load record</p>
          <p className="text-sm text-red-500">{error}</p>
        </div>
        <button
          onClick={refetch}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // Record not found — query succeeded but no data returned.
  if (notFound || !record) {
    return (
      <div className="py-8 text-center">
        <p className="text-slate-600 mb-2">
          {config.displayName} not found
        </p>
        <p className="text-sm text-slate-400 mb-4">
          The record may have been deleted or the ID is invalid.
        </p>
        <Link
          to={`/${entityName}`}
          className="text-blue-600 hover:text-blue-800 underline"
        >
          Back to {config.displayName} list
        </Link>
      </div>
    );
  }

  async function handleDelete() {
    try {
      await deleteEntity(entityName!, id!);
      showToast(`${config!.displayName} deleted successfully`);
      navigate(`/${entityName}`);
    } catch (err: any) {
      showToast(err.message, 'error');
    }
    setShowDelete(false);
  }

  // Build columns from all attributes for detail view
  const allColumns = record.Attributes.map(attr => {
    const formField = config.formFields.find(f => f.key === attr.Key);
    const listCol = config.listColumns.find(c => c.key === attr.Key);
    return {
      key: attr.Key,
      label: formField?.label ?? listCol?.label ?? attr.Key,
      type: formField?.type ?? listCol?.type ?? 'text' as const,
      targetEntity: formField?.targetEntity ?? listCol?.targetEntity,
      optionSetKey: formField?.optionSetKey ?? listCol?.optionSetKey,
    };
  });

  return (
    <>
      <EntityDetail
        record={record}
        columns={allColumns}
        entityName={entityName!}
        displayName={config.displayName}
        onDelete={() => setShowDelete(true)}
      />
      {config.relatedEntities && config.relatedEntities.length > 0 && (
        <RelatedEntitiesTabs
          parentId={record.Id}
          relatedEntities={config.relatedEntities}
        />
      )}
      <DeleteDialog
        entityName={config.displayName}
        isOpen={showDelete}
        onConfirm={handleDelete}
        onCancel={() => setShowDelete(false)}
      />
    </>
  );
}
